import { useState } from 'react';
import { dateDuJour, formaterDate } from '@gsm/partage';
import { api, ErreurApi } from '../api/client';
import { messageErreur } from '../api/ressource';
import { Bloc, Tableau } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Champ } from '../composants/Champ';
import { DepotFichierTexte } from '../composants/DepotFichierTexte';

interface Anomalie {
  code: string;
  message: string;
}

interface Resultat {
  date: string;
  enregistres: number;
  anomalies: Anomalie[];
}

/** Une anomalie de cotation n'est pas un refus : elle appelle un arbitrage. */
const BLOQUANTES = new Set(['PRIX_MANQUANT', 'PRIX_INVALIDE']);

const EXEMPLE = `type;code;valeur
ACTION;ATW;512.400000
ACTION;IAM;108.500000
OPCVM;FCP-MON-01;1245.780000
INDICE;MASI;14235.220000`;

export function ImportCours() {
  const [dateValo, setDateValo] = useState(dateDuJour());
  const [texte, setTexte] = useState('');
  const [nomFichier, setNomFichier] = useState<string | null>(null);
  const [reporter, setReporter] = useState(true);
  const [resultat, setResultat] = useState<Resultat | null>(null);
  const [valorisation, setValorisation] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  /** Analyse du CSV : `;` en séparateur, une ligne d'en-tête tolérée. */
  function analyser(contenu: string) {
    return contenu
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l && !l.startsWith('#'))
      .map((l) => l.split(';').map((c) => c.trim()))
      .filter((c) => c.length >= 3 && c[0]?.toUpperCase() !== 'TYPE')
      .map((c) => ({ type: c[0]!.toUpperCase(), code: c[1]!, valeur: c[2]! }));
  }

  async function importer() {
    setErreur(null);
    setResultat(null);
    setValorisation(null);
    setOccupe(true);
    try {
      const lignes = analyser(texte);
      if (lignes.length === 0) throw new Error('Aucune ligne exploitable.');
      setResultat(
        await api.post<Resultat>('/valorisations/prix', {
          dateValo,
          nomFichier: nomFichier ?? `saisie-${dateValo}.csv`,
          reporterManquants: reporter,
          lignes,
        }),
      );
    } catch (e) {
      setErreur(e instanceof ErreurApi ? messageErreur(e) : String(e));
    } finally {
      setOccupe(false);
    }
  }

  async function valoriser() {
    setErreur(null);
    setOccupe(true);
    try {
      const r = await api.post<{ dates: { date: string; mandats: number }[]; cascade: number }>(
        '/valorisations/calculer',
        { dateValo },
      );
      setValorisation(
        `${r.dates.length} date(s) valorisée(s)` +
          (r.cascade > 0 ? `, dont ${r.cascade} recalculée(s) en cascade` : '') +
          ` — ${r.dates[0]?.mandats ?? 0} mandat(s).`,
      );
    } catch (e) {
      setErreur(e instanceof ErreurApi ? messageErreur(e) : 'Valorisation impossible.');
    } finally {
      setOccupe(false);
    }
  }

  const bloquantes = (resultat?.anomalies ?? []).filter((a) => BLOQUANTES.has(a.code));

  return (
    <>
      <p className="entete__fil">Valorisation</p>
      <div className="entete">
        <h1>Import quotidien</h1>
        <span className="entete__ref">cours et indices</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Import refusé">
          {erreur}
        </BandeauAlerte>
      )}

      <Bloc titre="Fichier de valorisation" meta="type ; code ; valeur">
        <p className="note note--tete">
          Importez d’abord les prix, puis lancez le calcul. Recalculer une date passée reprend
          toutes les dates postérieures.
        </p>
        <div className="disposition-import">
          <div className="disposition-import__options">
            <Champ
              label="Date de valorisation"
              type="date"
              value={dateValo}
              onChange={(e) => setDateValo(e.target.value)}
            />
            <label className="case-a-cocher">
              <input
                type="checkbox"
                checked={reporter}
                onChange={(e) => setReporter(e.target.checked)}
              />
              Reporter les prix manquants
            </label>
          </div>

          <div className="disposition-import__fichier">
            <DepotFichierTexte
              nomFichier={nomFichier}
              onFichier={(contenu, nom) => {
                setTexte(contenu);
                setNomFichier(nom);
                setResultat(null);
                setErreur(null);
              }}
              onRetirer={() => {
                setTexte('');
                setNomFichier(null);
              }}
              aide="Le fichier de cours du jour, tel quel. Son contenu s’affiche ci-dessous et reste modifiable avant envoi."
              desactive={occupe}
            />

            <label className="surtitre" htmlFor="contenu">
              Contenu du fichier
            </label>
            <textarea
              id="contenu"
              value={texte}
              onChange={(e) => {
                setTexte(e.target.value);
                // Retouché à la main, ce n'est plus le fichier reçu.
                setNomFichier(null);
              }}
              placeholder={EXEMPLE}
              rows={10}
              className="zone-texte"
            />
          </div>
        </div>

        <div className="barre-actions barre-actions--apres">
          <button
            className="bouton"
            disabled={occupe || !texte.trim()}
            onClick={() => void importer()}
          >
            {occupe ? 'En cours…' : 'Importer les prix'}
          </button>
          <button
            className="bouton bouton--fantome"
            disabled={occupe}
            onClick={() => void valoriser()}
          >
            Lancer la valorisation
          </button>
        </div>
      </Bloc>

      {resultat && (
        <Bloc
          titre="Résultat de l’import"
          meta={`${resultat.enregistres} prix au ${formaterDate(resultat.date)}`}
        >
          {bloquantes.length > 0 && (
            <BandeauAlerte nature="refus" titre="Valorisation impossible en l’état">
              {bloquantes.length} instrument(s) détenu(s) sans prix : la valorisation serait fausse.
            </BandeauAlerte>
          )}
          <Tableau
            colonnes={[
              { cle: 'code', entete: 'Code', rendu: (a: Anomalie) => a.code },
              { cle: 'message', entete: 'Anomalie', rendu: (a: Anomalie) => a.message },
            ]}
            lignes={resultat.anomalies}
            cle={(_, i) => String(i)}
            vide="Aucune anomalie : tous les instruments détenus ont un prix."
          />
        </Bloc>
      )}

      {valorisation && (
        <BandeauAlerte nature="information" titre="Valorisation terminée">
          {valorisation}
        </BandeauAlerte>
      )}
    </>
  );
}
